const resources = {
  "en": {
    "recurrentIncomeManager": {
      "title": "Recurring incomes",
      "description": "Keep repeating income flows legible and stable while preserving the existing forecasting and edit behavior.",
      "headerMeta": "Recurring income schedules",
      "registryTitle": "Income registry",
      "registryDescription": "Review saved income setups, confirm target accounts, and open the existing create or edit flows.",
      "metrics": {
        "totalSetups": "Income setups",
        "linkedAccountsMeta": "{{count}} target accounts linked",
        "preTaxSetups": "Pre-tax setups",
        "afterTaxMeta": "{{count}} post-tax setups",
        "plannedTotal": "Planned total",
        "plannedTotalMeta": "{{count}} currency view(s)"
      },
      "accessibility": {
        "actions": "Recurring income actions"
      },
      "actions": {
        "add": "Add",
        "edit": "Edit",
        "pause": "Pause",
        "resume": "Resume",
        "delete": "Delete",
        "title": "Tools",
        "description": "Use the existing income forecast preview without changing current recurring setups.",
        "viewForecast": "View forecast",
        "openMenu": "Open income actions",
        "closeMenu": "Close income actions"
      },
      "list": {
        "amount": "💵 {{amount}}",
        "frequency": "🔁 {{frequency}} starting {{date}}",
        "taxStatus": "🧾 {{status}}",
        "empty": "No recurrent incomes yet",
        "loading": "Loading recurring incomes…",
        "error": "Unable to display recurring incomes right now."
      },
      "status": {
        "active": "Active",
        "paused": "Paused"
      },
      "summary": {
        "title": "Summary",
        "description": "Track saved recurring income totals across currencies while respecting amount visibility settings.",
        "total": "Total: {{amount}}",
        "totalByCurrency": "Total ({{currency}}): {{amount}}",
        "items": "Items: {{count}}"
      },
      "form": {
        "editTitle": "Edit Recurrent Income",
        "addTitle": "Add Recurrent Income",
        "save": "Save",
        "description": "Description",
        "descriptionPlaceholder": "e.g. Paycheck, Dividends",
        "amount": "Amount",
        "amountPlaceholder": "Enter amount",
        "amountHint": "Decimals are supported. Use a dot, for example: 12.34",
        "category": "Business category",
        "categoryPlaceholder": "Select what this income means",
        "startDate": "Start Date",
        "startDatePlaceholder": "Select start date",
        "frequency": "Frequency",
        "frequencyPlaceholder": "Select frequency",
        "targetAccount": "Target account",
        "targetAccountPlaceholder": "Select an account to receive this income",
        "accountLabel": "{{institution}} — {{type}}",
        "accountRequired": "Please select an account",
        "accountInvalid": "Please select a valid account",
        "accountGoalNotAllowed": "Goal vault account cannot be used for recurring incomes. Please select another account.",
        "accountCurrencyRequired": "Selected account must include a currency",
        "accountMeta": "Currency: {{currency}} • Asset type: {{assetType}}",
        "accountCurrencyOnly": "Currency: {{currency}}",
        "currencyMissingShort": "Currency missing",
        "taxStatus": "Tax Status",
        "taxStatusPlaceholder": "Select tax status",
        "cancel": "Cancel",
        "submit": "Submit"
      },
      "fallbacks": {
        "unknownAccount": "Unknown account"
      },
      "frequency": {
        "daily": "Daily",
        "weekly": "Weekly",
        "biWeekly": "Bi-weekly",
        "monthly": "Monthly",
        "yearly": "Yearly"
      },
      "taxStatus": {
        "postTax": "Post-tax",
        "preTax": "Pre-tax"
      },
      "popconfirm": {
        "title": "Delete this recurring income?",
        "description": "This action cannot be undone.",
        "okText": "Delete",
        "cancelText": "Cancel"
      },
      "messages": {
        "errorWithReason": "❌ {{error}}",
        "loadIncomesError": "❌ Failed to load recurrent incomes",
        "addSuccess": "✅ Income added",
        "updateSuccess": "✅ Income updated",
        "deleteSuccess": "✅ Income deleted",
        "pauseSuccess": "Recurring income paused",
        "resumeSuccess": "Recurring income resumed",
        "submitError": "❌ Unexpected error",
        "deleteError": "❌ Delete failed",
        "lifecycleError": "Unable to update recurring income status",
        "loadAccountsError": "❌ Failed to load accounts",
        "loadError": "Unable to load recurring incomes.",
        "accountCurrencyRequired": "Selected account must include a currency"
      }
    }
  },
  "uk": {
    "recurrentIncomeManager": {
      "title": "Регулярні доходи",
      "description": "Керуйте повторюваними доходами спокійно та прозоро, не змінюючи наявний forecast та edit flow.",
      "headerMeta": "Розклади регулярних доходів",
      "registryTitle": "Реєстр доходів",
      "registryDescription": "Переглядайте збережені налаштування доходів, перевіряйте цільові рахунки та відкривайте наявні create/edit флоу.",
      "metrics": {
        "totalSetups": "Налаштування доходів",
        "linkedAccountsMeta": "{{count}} цільових рахунків пов’язано",
        "preTaxSetups": "Налаштування до оподаткування",
        "afterTaxMeta": "{{count}} після оподаткування",
        "plannedTotal": "Запланована сума",
        "plannedTotalMeta": "{{count}} валютних представлень"
      },
      "accessibility": {
        "actions": "Дії з регулярними доходами"
      },
      "actions": {
        "add": "Додати",
        "edit": "Редагувати",
        "pause": "Призупинити",
        "resume": "Відновити",
        "delete": "Видалити",
        "title": "Інструменти",
        "description": "Відкривайте наявний прогноз доходів без змін у поточних регулярних налаштуваннях.",
        "viewForecast": "Переглянути прогноз",
        "openMenu": "Відкрити дії з доходом",
        "closeMenu": "Закрити дії з доходом"
      },
      "list": {
        "amount": "💵 {{amount}}",
        "frequency": "🔁 {{frequency}} з {{date}}",
        "taxStatus": "🧾 {{status}}",
        "empty": "Регулярних доходів поки немає",
        "loading": "Завантаження регулярних доходів…",
        "error": "Зараз не вдається показати регулярні доходи."
      },
      "status": {
        "active": "Активний",
        "paused": "Призупинений"
      },
      "summary": {
        "title": "Підсумок",
        "description": "Відстежуйте збережені регулярні доходи в різних валютах з урахуванням режиму приховування сум.",
        "total": "Разом: {{amount}}",
        "totalByCurrency": "Разом ({{currency}}): {{amount}}",
        "items": "Записи: {{count}}"
      },
      "form": {
        "editTitle": "Редагувати регулярний дохід",
        "addTitle": "Додати регулярний дохід",
        "save": "Зберегти",
        "description": "Опис",
        "descriptionPlaceholder": "наприклад, Зарплата, Дивіденди",
        "amount": "Сума",
        "amountPlaceholder": "Введіть суму",
        "amountHint": "Дробова частина підтримується. Використовуйте крапку, наприклад: 12.34",
        "category": "Бізнес-категорія",
        "categoryPlaceholder": "Оберіть, що означає цей дохід",
        "startDate": "Дата початку",
        "startDatePlaceholder": "Оберіть дату початку",
        "frequency": "Частота",
        "frequencyPlaceholder": "Виберіть частоту",
        "targetAccount": "Цільовий рахунок",
        "targetAccountPlaceholder": "Виберіть рахунок для зарахування доходу",
        "accountLabel": "{{institution}} — {{type}}",
        "accountRequired": "Будь ласка, виберіть рахунок",
        "accountInvalid": "Будь ласка, виберіть дійсний рахунок",
        "accountGoalNotAllowed": "Рахунок Goal Vault не можна використовувати для регулярних доходів. Будь ласка, виберіть інший рахунок.",
        "accountCurrencyRequired": "Обраний рахунок повинен мати валюту",
        "accountMeta": "Валюта: {{currency}} • Тип активу: {{assetType}}",
        "accountCurrencyOnly": "Валюта: {{currency}}",
        "currencyMissingShort": "Валюта відсутня",
        "taxStatus": "Статус оподаткування",
        "taxStatusPlaceholder": "Оберіть статус оподаткування",
        "cancel": "Скасувати",
        "submit": "Підтвердити"
      },
      "fallbacks": {
        "unknownAccount": "Невідомий рахунок"
      },
      "frequency": {
        "daily": "Щодня",
        "weekly": "Щотижня",
        "biWeekly": "Раз на два тижні",
        "monthly": "Щомісяця",
        "yearly": "Щороку"
      },
      "taxStatus": {
        "postTax": "Після сплати податків",
        "preTax": "До сплати податків"
      },
      "popconfirm": {
        "title": "Видалити цей регулярний дохід?",
        "description": "Цю дію неможливо скасувати.",
        "okText": "Видалити",
        "cancelText": "Скасувати"
      },
      "messages": {
        "errorWithReason": "❌ {{error}}",
        "loadIncomesError": "❌ Не вдалося завантажити регулярні доходи",
        "addSuccess": "✅ Дохід додано",
        "updateSuccess": "✅ Дохід оновлено",
        "deleteSuccess": "✅ Дохід видалено",
        "pauseSuccess": "Регулярний дохід призупинено",
        "resumeSuccess": "Регулярний дохід відновлено",
        "submitError": "❌ Неочікувана помилка",
        "deleteError": "❌ Помилка видалення",
        "lifecycleError": "Не вдалося оновити статус регулярного доходу",
        "loadAccountsError": "❌ Не вдалося завантажити рахунки",
        "loadError": "Не вдалося завантажити регулярні доходи.",
        "accountCurrencyRequired": "Обраний рахунок повинен мати валюту"
      }
    }
  }
};

export default resources;
